import * as THREE from "three";
import {
  ARENA,
  ARROW_TYPES,
  CREATURE_TYPES,
  FACTION_COLOR,
  FACTION_SPAWN,
} from "@shared/constants";
import type {
  Arrow,
  ArrowType,
  ArenaState,
  Creature,
  Faction,
  Player,
  Vec3,
} from "@shared/types";

// ── Public API ────────────────────────────────────────────────────────────────

export interface SceneHandle {
  dispose: () => void;
  /** update the world from a fresh server snapshot */
  applyState: (state: ArenaState, selfId: string | null) => void;
  /** set the local player's desired world position (from WASD) */
  setDesiredPos: (x: number, z: number) => void;
  /** server-authoritative self position; used as a soft correction, not the render source */
  setSelfCorrection: (x: number, z: number) => void;
  /** raycast the crosshair; returns world point + optional creature/player id */
  pickTarget: () => { pos: Vec3; targetId: string | null } | null;
  /** camera follows this player */
  setSelf: (playerId: string | null) => void;
  getCameraPos: () => THREE.Vector3;
  /** project a world (x,z) point to screen pixels, or null if behind camera */
  projectToScreen: (x: number, z: number) => { x: number; y: number } | null;
}

interface EntityVisual {
  group: THREE.Group;
  hpBar: THREE.Group;
  hpFill: THREE.Mesh;
  hpFillMat: THREE.MeshBasicMaterial;
  target: { id: string; kind: "player" | "creature"; faction: Faction };
  baseY: number;
  bobPhase: number;
  /** interpolation buffer: render between prev and target for smoothness */
  prevX: number;
  prevZ: number;
  targetX: number;
  targetZ: number;
  prevAt: number;
  targetAt: number;
  interpInit: boolean;
}

interface ArrowVisual {
  group: THREE.Group;
  line: THREE.Line;
  arrow: Arrow;
  type: ArrowType;
  impacted: boolean;
  /** rolling flight trail (world-space points, oldest first) */
  trail: THREE.Vector3[];
}

interface ImpactFx {
  group: THREE.Group;
  bornAt: number;
  ttl: number;
  kind: string;
}

const ARROW_TRAIL_LEN = 14;
/** how far in the past we render remote entities (ms) to smooth polling.
 *  Sized to span a full production RTT (~500ms) so the lerp hides the gap. */
const INTERP_DELAY_MS = 250;
/** below this distance the server position gently corrects the local prediction;
 *  above it we snap (clamped / rejected move). */
const SOFT_CORRECT_DIST = 3;
/** per-frame fraction of the correction error applied (dt-scaled in animate) */
const SOFT_CORRECT_RATE = 0.12;

export function createBattlefieldScene(
  canvas: HTMLCanvasElement,
): SceneHandle {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  // The canvas may not be laid out to its final size when this runs (React
  // mounts before layout), so size from the parent element and re-sync on
  // layout changes.
  const parent = canvas.parentElement;
  const sizeFrom = () => {
    const w = parent?.clientWidth || canvas.clientWidth || window.innerWidth;
    const h = parent?.clientHeight || canvas.clientHeight || window.innerHeight;
    return { w: Math.max(1, w), h: Math.max(1, h) };
  };
  const { w: initW, h: initH } = sizeFrom();
  renderer.setSize(initW, initH, false);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0a0f1a);
  scene.fog = new THREE.Fog(0x0a0f1a, 80, 220);

  const camera = new THREE.PerspectiveCamera(50, initW / initH, 0.1, 800);
  camera.position.set(0, 95, 125);

  // ── Lights ──
  const ambient = new THREE.AmbientLight(0x8a9ac0, 1.8);
  scene.add(ambient);
  const hemi = new THREE.HemisphereLight(0xbfd0f0, 0x3a4a62, 1.3);
  scene.add(hemi);
  const moon = new THREE.DirectionalLight(0xe0ecff, 2.4);
  moon.position.set(-40, 100, 50);
  scene.add(moon);
  const dawnLight = new THREE.PointLight(0xf59e0b, 2, 80);
  dawnLight.position.set(FACTION_SPAWN.dawn.x, 12, 0);
  scene.add(dawnLight);
  const duskLight = new THREE.PointLight(0x8b5cf6, 2, 80);
  duskLight.position.set(FACTION_SPAWN.dusk.x, 12, 0);
  scene.add(duskLight);

  buildTerrain(scene);
  buildBanners(scene);

  // ── Entity pools ──
  const entities = new Map<string, EntityVisual>();
  const arrows = new Map<string, ArrowVisual>();
  const fx: ImpactFx[] = [];

  let selfId: string | null = null;
  let desiredPos: { x: number; z: number } | null = null;
  /** latest server-authoritative self position (correction source, not render source) */
  let selfCorrection: { x: number; z: number } | null = null;
  const selfSmooth = new THREE.Vector3();
  let hasSelf = false;

  // ── Terrain ──
  function buildTerrain(scene: THREE.Scene) {
    const groundGeo = new THREE.PlaneGeometry(ARENA.WIDTH, ARENA.DEPTH, 40, 40);
    const pos = groundGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const d = Math.sqrt(x * x + y * y);
      if (d > ARENA.NEUTRAL_RADIUS) {
        pos.setZ(i, Math.sin(x * 0.3) * Math.cos(y * 0.3) * 0.6 + Math.random() * 0.15);
      } else {
        pos.setZ(i, 0);
      }
    }
    groundGeo.computeVertexNormals();
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x3a4f6f,
      roughness: 0.85,
      metalness: 0.05,
      flatShading: true,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = ARENA.GROUND_Y;
    scene.add(ground);

    // neutral zone ring
    const ringGeo = new THREE.RingGeometry(ARENA.NEUTRAL_RADIUS - 0.5, ARENA.NEUTRAL_RADIUS, 48);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x475569,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.05;
    scene.add(ring);

    // faction tinted halves
    const dawnHalf = new THREE.Mesh(
      new THREE.PlaneGeometry(ARENA.WIDTH / 2 - ARENA.NEUTRAL_RADIUS, ARENA.DEPTH),
      new THREE.MeshBasicMaterial({ color: 0xf59e0b, transparent: true, opacity: 0.09 }),
    );
    dawnHalf.rotation.x = -Math.PI / 2;
    dawnHalf.position.set(-ARENA.WIDTH / 4 - ARENA.NEUTRAL_RADIUS / 2, 0.02, 0);
    scene.add(dawnHalf);

    const duskHalf = new THREE.Mesh(
      new THREE.PlaneGeometry(ARENA.WIDTH / 2 - ARENA.NEUTRAL_RADIUS, ARENA.DEPTH),
      new THREE.MeshBasicMaterial({ color: 0x8b5cf6, transparent: true, opacity: 0.09 }),
    );
    duskHalf.rotation.x = -Math.PI / 2;
    duskHalf.position.set(ARENA.WIDTH / 4 + ARENA.NEUTRAL_RADIUS / 2, 0.02, 0);
    scene.add(duskHalf);

    // scattered rocks
    const rockGeo = new THREE.DodecahedronGeometry(1, 0);
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 1 });
    for (let i = 0; i < 40; i++) {
      const rock = new THREE.Mesh(rockGeo, rockMat);
      const x = (Math.random() - 0.5) * ARENA.WIDTH * 0.9;
      const z = (Math.random() - 0.5) * ARENA.DEPTH * 0.9;
      if (Math.sqrt(x * x + z * z) < ARENA.NEUTRAL_RADIUS + 3) continue;
      rock.position.set(x, 0.3, z);
      rock.scale.setScalar(0.3 + Math.random() * 1.2);
      rock.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      scene.add(rock);
    }

    // boundary walls (low, glowing)
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x1e293b,
      emissive: 0x1e3a5f,
      emissiveIntensity: 0.4,
    });
    const wallH = 2;
    const mkWall = (w: number, d: number, x: number, z: number) => {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(w, wallH, d), wallMat);
      wall.position.set(x, wallH / 2, z);
      scene.add(wall);
    };
    mkWall(ARENA.WIDTH, 1, 0, -ARENA.DEPTH / 2);
    mkWall(ARENA.WIDTH, 1, 0, ARENA.DEPTH / 2);
    mkWall(1, ARENA.DEPTH, -ARENA.WIDTH / 2, 0);
    mkWall(1, ARENA.DEPTH, ARENA.WIDTH / 2, 0);
  }

  function buildBanners(scene: THREE.Scene) {
    for (const faction of ["dawn", "dusk"] as Faction[]) {
      const spawn = FACTION_SPAWN[faction];
      const color = FACTION_COLOR[faction];

      // banner pole
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15, 0.2, 14, 8),
        new THREE.MeshStandardMaterial({ color: 0x374151 }),
      );
      pole.position.set(spawn.x, 7, 0);
      scene.add(pole);

      // banner flag
      const flag = new THREE.Mesh(
        new THREE.PlaneGeometry(5, 3),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 0.3,
          side: THREE.DoubleSide,
        }),
      );
      flag.position.set(spawn.x + 2.5, 11, 0);
      scene.add(flag);

      // spawn pad
      const pad = new THREE.Mesh(
        new THREE.CylinderGeometry(16, 16, 0.3, 32),
        new THREE.MeshStandardMaterial({
          color,
          transparent: true,
          opacity: 0.15,
          emissive: color,
          emissiveIntensity: 0.2,
        }),
      );
      pad.position.set(spawn.x, 0.15, 0);
      scene.add(pad);
    }
  }

  // ── Entity visuals ──
  function makeHpBar(width: number): { bar: THREE.Group; fill: THREE.Mesh; fillMat: THREE.MeshBasicMaterial } {
    const bar = new THREE.Group();
    const bg = new THREE.Mesh(
      new THREE.PlaneGeometry(width, 0.3),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.6 }),
    );
    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry(width, 0.3),
      new THREE.MeshBasicMaterial({ color: 0x22c55e }),
    );
    fill.position.z = 0.01;
    bar.add(bg);
    bar.add(fill);
    return { bar, fill, fillMat: fill.material as THREE.MeshBasicMaterial };
  }

  function buildPlayerMesh(p: Player): THREE.Group {
    const g = new THREE.Group();
    const color = FACTION_COLOR[p.faction];

    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.5, 1.2, 4, 8),
      new THREE.MeshStandardMaterial({ color, roughness: 0.6 }),
    );
    body.position.y = 1.2;
    g.add(body);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 12, 12),
      new THREE.MeshStandardMaterial({ color: 0xfde68a }),
    );
    head.position.y = 2.3;
    g.add(head);

    // bow (torus arc on the side)
    const bow = new THREE.Mesh(
      new THREE.TorusGeometry(0.8, 0.06, 8, 16, Math.PI),
      new THREE.MeshStandardMaterial({ color: 0x92400e }),
    );
    bow.position.set(0.6, 1.4, 0);
    bow.rotation.z = Math.PI / 2;
    g.add(bow);
    return g;
  }

  function buildCreatureMesh(c: Creature): THREE.Group {
    const g = new THREE.Group();
    const spec = CREATURE_TYPES[c.type];
    const color = spec.color;
    const s = spec.scale;

    if (c.type === "goblin") {
      const body = new THREE.Mesh(
        new THREE.ConeGeometry(0.6 * s, 1.6 * s, 8),
        new THREE.MeshStandardMaterial({ color, roughness: 0.8 }),
      );
      body.position.y = 0.8 * s;
      g.add(body);
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.1 * s, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xff0000 }),
      );
      eye.position.set(0, 1.2 * s, 0.4 * s);
      g.add(eye);
    } else if (c.type === "ghoul") {
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(1 * s, 1.8 * s, 0.8 * s),
        new THREE.MeshStandardMaterial({ color, roughness: 0.9 }),
      );
      body.position.y = 0.9 * s;
      g.add(body);
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.4 * s, 10, 10),
        new THREE.MeshStandardMaterial({ color: 0xcbd5e1 }),
      );
      head.position.y = 2 * s;
      g.add(head);
      // tattered arms
      for (const side of [-1, 1]) {
        const arm = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08 * s, 0.12 * s, 1.2 * s, 6),
          new THREE.MeshStandardMaterial({ color }),
        );
        arm.position.set(side * 0.6 * s, 1.2 * s, 0);
        arm.rotation.z = side * 0.5;
        g.add(arm);
      }
    } else {
      // bug: low wide body + legs
      const body = new THREE.Mesh(
        new THREE.SphereGeometry(0.5 * s, 8, 6),
        new THREE.MeshStandardMaterial({ color, roughness: 0.7 }),
      );
      body.scale.y = 0.6;
      body.position.y = 0.4 * s;
      g.add(body);
      for (let i = 0; i < 6; i++) {
        const leg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.03 * s, 0.05 * s, 0.6 * s, 4),
          new THREE.MeshStandardMaterial({ color: 0x451a03 }),
        );
        const angle = (i / 6) * Math.PI * 2;
        leg.position.set(Math.cos(angle) * 0.4 * s, 0.2 * s, Math.sin(angle) * 0.4 * s);
        leg.rotation.set(Math.sin(angle) * 0.8, 0, -Math.cos(angle) * 0.8);
        g.add(leg);
      }
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.08 * s, 6, 6),
        new THREE.MeshBasicMaterial({ color: 0xffaa00 }),
      );
      eye.position.set(0, 0.5 * s, 0.4 * s);
      g.add(eye);
    }

    // faction tint ring at feet
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.5 * s, 0.7 * s, 16),
      new THREE.MeshBasicMaterial({
        color: FACTION_COLOR[c.faction],
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03;
    g.add(ring);

    return g;
  }

  function ensureEntity(id: string, kind: "player" | "creature", faction: Faction, mesh: THREE.Group, hp: number, maxHp: number, baseY: number): EntityVisual {
    const existing = entities.get(id);
    if (existing) {
      updateHpBar(existing, hp, maxHp);
      return existing;
    }
    const { bar, fill, fillMat } = makeHpBar(1.6);
    bar.position.y = baseY + 2.6;
    mesh.add(bar);
    const ev: EntityVisual = {
      group: mesh,
      hpBar: bar,
      hpFill: fill,
      hpFillMat: fillMat,
      target: { id, kind, faction },
      baseY,
      bobPhase: Math.random() * Math.PI * 2,
      prevX: mesh.position.x,
      prevZ: mesh.position.z,
      targetX: mesh.position.x,
      targetZ: mesh.position.z,
      prevAt: 0,
      targetAt: 0,
      interpInit: false,
    };
    entities.set(id, ev);
    scene.add(mesh);
    updateHpBar(ev, hp, maxHp);
    return ev;
  }

  function updateHpBar(ev: EntityVisual, hp: number, maxHp: number) {
    const t = Math.max(0, hp / maxHp);
    ev.hpFill.scale.x = t;
    ev.hpFill.position.x = -(1 - t) * 0.8;
    ev.hpFillMat.color.setHSL(t * 0.33, 0.8, 0.5);
  }

  // ── Arrows ──
  function buildArrowMesh(type: ArrowType): THREE.Group {
    const color = ARROW_TYPES[type].color;
    const group = new THREE.Group();

    const headMat = new THREE.MeshBasicMaterial({ color });
    const shaftMat = new THREE.MeshBasicMaterial({ color });

    // shaft (all types)
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.2, 6), shaftMat);
    shaft.rotation.z = Math.PI / 2;
    group.add(shaft);

    // head (all types)
    const head = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.28, 8), headMat);
    head.rotation.z = -Math.PI / 2;
    head.position.x = 0.7;
    group.add(head);

    // per-type signature
    if (type === "fire") {
      const flameMat = new THREE.MeshBasicMaterial({ color: 0xffb347, transparent: true, opacity: 0.9 });
      for (let i = 0; i < 3; i++) {
        const flame = new THREE.Mesh(new THREE.ConeGeometry(0.12 - i * 0.03, 0.4 - i * 0.1, 6), flameMat);
        flame.rotation.z = -Math.PI / 2;
        flame.position.set(0.55 + i * 0.12, 0, 0);
        flame.name = `flame${i}`;
        group.add(flame);
      }
    } else if (type === "frost") {
      const iceMat = new THREE.MeshBasicMaterial({ color: 0xbae6fd, transparent: true, opacity: 0.85 });
      for (let i = 0; i < 4; i++) {
        const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.08), iceMat);
        const a = (i / 4) * Math.PI * 2;
        shard.position.set(0.55, Math.cos(a) * 0.18, Math.sin(a) * 0.18);
        shard.name = `shard${i}`;
        group.add(shard);
      }
    } else if (type === "venom") {
      const dripMat = new THREE.MeshBasicMaterial({ color: 0x16a34a, transparent: true, opacity: 0.9 });
      for (let i = 0; i < 3; i++) {
        const drip = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 6), dripMat);
        drip.scale.y = 1.6;
        drip.position.set(0.4 - i * 0.15, -0.12 - i * 0.04, 0);
        drip.name = `drip${i}`;
        group.add(drip);
      }
    } else if (type === "lightning") {
      const core = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.05, 1.2, 6),
        new THREE.MeshBasicMaterial({ color: 0xfff7c2, transparent: true, opacity: 0.9 }),
      );
      core.rotation.z = Math.PI / 2;
      core.name = "boltcore";
      group.add(core);
      const halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.22, 0.03, 6, 16),
        new THREE.MeshBasicMaterial({ color: 0xfacc15, transparent: true, opacity: 0.7 }),
      );
      halo.name = "bolthalo";
      group.add(halo);
    } else if (type === "starfall") {
      const starMat = new THREE.MeshBasicMaterial({ color: 0xd8b4fe, transparent: true, opacity: 0.9 });
      for (let i = 0; i < 3; i++) {
        const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.1), starMat);
        const a = (i / 3) * Math.PI * 2;
        shard.position.set(0.5, Math.cos(a) * 0.22, Math.sin(a) * 0.22);
        shard.name = `stardust${i}`;
        group.add(shard);
      }
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0xffffff }),
      );
      core.position.x = 0.5;
      group.add(core);
    } else if (type === "chaos") {
      const swirlMat = new THREE.MeshBasicMaterial({ color: 0xf9a8d4, transparent: true, opacity: 0.85 });
      for (let i = 0; i < 2; i++) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(0.18 + i * 0.1, 0.03, 6, 16),
          swirlMat,
        );
        ring.rotation.x = Math.PI / 2;
        ring.position.x = 0.4 - i * 0.1;
        ring.name = `chaosring${i}`;
        group.add(ring);
      }
    }

    // glow light (stronger for magical types)
    const intensity = type === "standard" ? 1 : type === "chaos" || type === "starfall" ? 3 : 2;
    const glow = new THREE.PointLight(color, intensity, 7);
    group.add(glow);

    return group;
  }

  function ensureArrowVisual(arrow: Arrow): ArrowVisual {
    let av = arrows.get(arrow.id);
    if (av) {
      av.arrow = arrow;
      return av;
    }
    const color = ARROW_TYPES[arrow.type].color;
    const group = buildArrowMesh(arrow.type);

    // trail line (rolling world-space points)
    const trailGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const trail = new THREE.Line(
      trailGeo,
      new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.7 }),
    );
    scene.add(trail);

    scene.add(group);
    av = { group, line: trail, arrow, type: arrow.type, impacted: false, trail: [] };
    arrows.set(arrow.id, av);
    return av;
  }

  // ── Impact FX ──
  const FX_COLORS: Record<string, number> = {
    fire: 0xf97316,
    frost: 0x38bdf8,
    venom: 0x4ade80,
    lightning: 0xfacc15,
    starfall: 0xa78bfa,
    chaos: 0xf472b6,
    standard: 0xd1d5db,
  };

  function makeFxGroup(pos: Vec3): THREE.Group {
    const group = new THREE.Group();
    group.position.set(pos.x, 0.4, pos.z);
    return group;
  }

  function addExpandingRing(group: THREE.Group, color: number, inner: number, outer: number) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(inner, outer, 28),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.name = "fxring";
    group.add(ring);
  }

  function spawnImpactFx(pos: Vec3, kind: string, isCrit: boolean) {
    const color = FX_COLORS[kind] ?? 0xffffff;
    const group = makeFxGroup(pos);

    if (kind === "fire") {
      // rising fireball + embers
      const fireMat = new THREE.MeshBasicMaterial({ color: 0xff7b24, transparent: true, opacity: 0.95 });
      const core = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 12), fireMat);
      core.name = "fxcore";
      group.add(core);
      const emberMat = new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 1 });
      for (let i = 0; i < 10; i++) {
        const e = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 6), emberMat);
        const a = (i / 10) * Math.PI * 2;
        e.position.set(Math.cos(a) * 0.4, 0.2 + Math.random() * 0.3, Math.sin(a) * 0.4);
        e.userData.vy = 0.02 + Math.random() * 0.02;
        group.add(e);
      }
      addExpandingRing(group, color, 0.2, 0.5);
    } else if (kind === "frost") {
      // ice shards shooting outward + frost ring
      const shardMat = new THREE.MeshBasicMaterial({ color: 0xbae6fd, transparent: true, opacity: 0.95 });
      for (let i = 0; i < 12; i++) {
        const s = new THREE.Mesh(new THREE.OctahedronGeometry(0.12), shardMat);
        const a = (i / 12) * Math.PI * 2;
        s.position.set(Math.cos(a) * 0.3, 0.3, Math.sin(a) * 0.3);
        s.userData.dir = new THREE.Vector3(Math.cos(a), 0.4, Math.sin(a));
        s.userData.spin = (Math.random() - 0.5) * 0.4;
        group.add(s);
      }
      addExpandingRing(group, color, 0.3, 0.7);
    } else if (kind === "venom") {
      // spreading toxic pool + bubbling droplets
      const poolMat = new THREE.MeshBasicMaterial({ color: 0x16a34a, transparent: true, opacity: 0.7, side: THREE.DoubleSide });
      const pool = new THREE.Mesh(new THREE.CircleGeometry(0.8, 24), poolMat);
      pool.rotation.x = -Math.PI / 2;
      pool.name = "fxpool";
      group.add(pool);
      const dropMat = new THREE.MeshBasicMaterial({ color: 0x86efac, transparent: true, opacity: 1 });
      for (let i = 0; i < 8; i++) {
        const d = new THREE.Mesh(new THREE.SphereGeometry(0.08, 6, 6), dropMat);
        const a = (i / 8) * Math.PI * 2;
        d.position.set(Math.cos(a) * 0.4, 0.3, Math.sin(a) * 0.4);
        d.userData.vy = -0.015;
        group.add(d);
      }
      addExpandingRing(group, color, 0.2, 0.45);
    } else if (kind === "lightning") {
      // jagged bolt from sky + flash
      const boltMat = new THREE.MeshBasicMaterial({ color: 0xfff7c2, transparent: true, opacity: 1 });
      const bolt = new THREE.Mesh(new THREE.ConeGeometry(0.15, 6, 5), boltMat);
      bolt.position.y = 3;
      bolt.name = "fxbolt";
      group.add(bolt);
      const flash = new THREE.Mesh(
        new THREE.SphereGeometry(0.6, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 }),
      );
      flash.name = "fxflash";
      group.add(flash);
      addExpandingRing(group, color, 0.2, 0.6);
    } else if (kind === "starfall") {
      // star explosion: big flash + radial shards + shockwave
      const coreMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 });
      const core = new THREE.Mesh(new THREE.SphereGeometry(0.7, 14, 14), coreMat);
      core.name = "fxcore";
      group.add(core);
      const shardMat = new THREE.MeshBasicMaterial({ color: 0xd8b4fe, transparent: true, opacity: 1 });
      for (let i = 0; i < 16; i++) {
        const s = new THREE.Mesh(new THREE.OctahedronGeometry(0.14), shardMat);
        const a = (i / 16) * Math.PI * 2;
        s.position.set(Math.cos(a) * 0.5, 0.4, Math.sin(a) * 0.5);
        s.userData.dir = new THREE.Vector3(Math.cos(a), 0.3, Math.sin(a));
        s.userData.spin = (Math.random() - 0.5) * 0.5;
        group.add(s);
      }
      addExpandingRing(group, color, 0.3, 0.9);
    } else if (kind === "chaos") {
      // swirling vortex: spinning rings + purple flash
      const ringMat = new THREE.MeshBasicMaterial({ color: 0xf9a8d4, transparent: true, opacity: 0.9, side: THREE.DoubleSide });
      for (let i = 0; i < 3; i++) {
        const r = new THREE.Mesh(new THREE.TorusGeometry(0.4 + i * 0.3, 0.05, 8, 24), ringMat);
        r.rotation.x = Math.PI / 2;
        r.name = `fxtorus${i}`;
        group.add(r);
      }
      const flash = new THREE.Mesh(
        new THREE.SphereGeometry(0.5, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xf472b6, transparent: true, opacity: 0.9 }),
      );
      flash.name = "fxflash";
      group.add(flash);
      addExpandingRing(group, color, 0.2, 0.6);
    } else {
      // standard: dust puff
      const puffMat = new THREE.MeshBasicMaterial({ color: 0x9ca3af, transparent: true, opacity: 0.8 });
      for (let i = 0; i < 8; i++) {
        const p = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 6), puffMat);
        const a = (i / 8) * Math.PI * 2;
        p.position.set(Math.cos(a) * 0.3, 0.2, Math.sin(a) * 0.3);
        p.userData.vy = 0.01;
        group.add(p);
      }
      addExpandingRing(group, 0x9ca3af, 0.15, 0.35);
    }

    // crit: bright white flash + bigger ring
    if (isCrit) {
      const critFlash = new THREE.Mesh(
        new THREE.SphereGeometry(1.1, 14, 14),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 }),
      );
      critFlash.name = "fxcritflash";
      group.add(critFlash);
      addExpandingRing(group, 0xffffff, 0.4, 1.0);
    }

    scene.add(group);
    const ttl = kind === "lightning" ? 320 : kind === "chaos" ? 700 : kind === "starfall" ? 600 : 500;
    fx.push({ group, bornAt: performance.now(), ttl, kind });
  }

  // ── Apply state ──
  function setEntityTarget(ev: EntityVisual, x: number, z: number) {
    const now = performance.now();
    if (!ev.interpInit) {
      // first sighting: start at the new position (no lerp from origin)
      ev.prevX = x;
      ev.prevZ = z;
      ev.targetX = x;
      ev.targetZ = z;
      ev.prevAt = now;
      ev.targetAt = now;
      ev.interpInit = true;
      return;
    }
    ev.prevX = ev.targetX;
    ev.prevZ = ev.targetZ;
    ev.targetX = x;
    ev.targetZ = z;
    ev.prevAt = ev.targetAt;
    ev.targetAt = now;
  }

  function applyState(state: ArenaState, _selfId: string | null) {
    const seen = new Set<string>();

    // players
    for (const p of state.players) {
      seen.add(p.id);
      let ev = entities.get(p.id);
      if (!ev || ev.target.kind !== "player") {
        if (ev) {
          scene.remove(ev.group);
          entities.delete(p.id);
        }
        const mesh = buildPlayerMesh(p);
        ev = ensureEntity(p.id, "player", p.faction, mesh, p.hp, p.maxHp, 2.8);
      }
      // Self is prediction-driven (see animate + setDesiredPos); do NOT push it
      // into the interp buffer, or the stale server position would override the
      // local prediction and cause rubber-banding.
      if (p.id !== selfId) {
        setEntityTarget(ev, p.pos.x, p.pos.z);
      }
      updateHpBar(ev, p.hp, p.maxHp);
      // hide dead players
      ev.group.visible = !p.isDead;
      // highlight self + enemies with a ring
      const isSelf = p.id === selfId;
      const isEnemy = selfId && p.faction !== entities.get(selfId)?.target.faction;
      let ring = ev.group.getObjectByName("selfring") as THREE.Mesh | undefined;
      if (isSelf || isEnemy) {
        if (!ring) {
          ring = new THREE.Mesh(
            new THREE.RingGeometry(0.8, 1.1, 24),
            new THREE.MeshBasicMaterial({
              color: isSelf ? 0xffffff : 0xef4444,
              transparent: true,
              opacity: 0.7,
              side: THREE.DoubleSide,
            }),
          );
          ring.name = "selfring";
          ring.rotation.x = -Math.PI / 2;
          ring.position.y = 0.05;
          ev.group.add(ring);
        }
        (ring.material as THREE.MeshBasicMaterial).color.set(isSelf ? 0xffffff : 0xef4444);
      } else if (ring) {
        ev.group.remove(ring);
      }
    }

    // creatures
    for (const c of state.creatures) {
      seen.add(c.id);
      let ev = entities.get(c.id);
      if (!ev || ev.target.kind !== "creature") {
        if (ev) {
          scene.remove(ev.group);
          entities.delete(c.id);
        }
        const mesh = buildCreatureMesh(c);
        ev = ensureEntity(c.id, "creature", c.faction, mesh, c.hp, c.maxHp, 2.2);
      }
      setEntityTarget(ev, c.pos.x, c.pos.z);
      updateHpBar(ev, c.hp, c.maxHp);
    }

    // remove stale entities
    for (const [id, ev] of entities) {
      if (!seen.has(id)) {
        scene.remove(ev.group);
        entities.delete(id);
      }
    }

    // arrows
    const seenArrows = new Set<string>();
    const nowMs = Date.now();
    for (const arrow of state.arrows) {
      seenArrows.add(arrow.id);
      const av = ensureArrowVisual(arrow);
      // interpolate origin -> target over flight duration (client-side smooth)
      const flightMs = arrow.impactAt - arrow.launchedAt;
      const elapsed = nowMs - arrow.launchedAt;
      const f = Math.min(1, Math.max(0, elapsed / Math.max(1, flightMs)));
      const x = arrow.origin.x + (arrow.target.x - arrow.origin.x) * f;
      const z = arrow.origin.z + (arrow.target.z - arrow.origin.z) * f;
      const y = 1.5 + Math.sin(f * Math.PI) * 3; // arc
      av.group.position.set(x, y, z);
      av.group.lookAt(arrow.target.x, y, arrow.target.z);

      // rolling flight trail
      if (!av.impacted) {
        av.trail.push(new THREE.Vector3(x, y, z));
        if (av.trail.length > ARROW_TRAIL_LEN) av.trail.shift();
      }
      const tpts = av.line.geometry.attributes.position as THREE.BufferAttribute;
      const n = av.trail.length;
      for (let i = 0; i < n; i++) {
        const p = av.trail[i];
        tpts.setXYZ(i, p.x, p.y, p.z);
      }
      av.line.geometry.setDrawRange(0, n);
      tpts.needsUpdate = true;

      // spawn impact FX once when the arrow lands
      if (f >= 1 && !av.impacted) {
        av.impacted = true;
        spawnImpactFx(arrow.target, arrow.type, arrow.crit);
      }
    }
    for (const [id, av] of arrows) {
      if (!seenArrows.has(id)) {
        scene.remove(av.group);
        scene.remove(av.line);
        arrows.delete(id);
      }
    }

    // FX cleanup + per-type animation
    const fxNow = performance.now();
    for (let i = fx.length - 1; i >= 0; i--) {
      const f = fx[i];
      const age = fxNow - f.bornAt;
      if (age > f.ttl) {
        scene.remove(f.group);
        fx.splice(i, 1);
        continue;
      }
      const t = age / f.ttl; // 0 -> 1
      const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic

      // generic fade for all materials
      f.group.traverse((obj) => {
        const m = obj as THREE.Mesh;
        if (m.material instanceof THREE.MeshBasicMaterial) {
          m.material.opacity = Math.max(0, 1 - t * t);
        }
      });

      // expanding ground ring
      const ring = f.group.getObjectByName("fxring") as THREE.Mesh | undefined;
      if (ring) ring.scale.setScalar(1 + ease * 2.5);

      switch (f.kind) {
        case "fire": {
          const core = f.group.getObjectByName("fxcore") as THREE.Mesh | undefined;
          if (core) core.position.y = ease * 2.2;
          f.group.children.forEach((c) => {
            const em = c as THREE.Mesh;
            if (em.userData.vy) em.position.y += em.userData.vy;
          });
          break;
        }
        case "frost":
        case "starfall": {
          f.group.children.forEach((c) => {
            const s = c as THREE.Mesh;
            if (s.userData.dir) {
              s.position.addScaledVector(s.userData.dir, 0.06);
              s.rotation.x += s.userData.spin;
              s.rotation.y += s.userData.spin;
            }
          });
          break;
        }
        case "venom": {
          const pool = f.group.getObjectByName("fxpool") as THREE.Mesh | undefined;
          if (pool) pool.scale.setScalar(1 + ease * 1.5);
          f.group.children.forEach((c) => {
            const d = c as THREE.Mesh;
            if (d.userData.vy) d.position.y += d.userData.vy;
          });
          break;
        }
        case "lightning": {
          const bolt = f.group.getObjectByName("fxbolt") as THREE.Mesh | undefined;
          if (bolt) bolt.scale.y = Math.max(0.05, 1 - t * 2); // retract
          const flash = f.group.getObjectByName("fxflash") as THREE.Mesh | undefined;
          if (flash) flash.scale.setScalar(Math.max(0.05, 1 - t * 1.5));
          break;
        }
        case "chaos": {
          for (let r = 0; r < 3; r++) {
            const tor = f.group.getObjectByName(`fxtorus${r}`) as THREE.Mesh | undefined;
            if (tor) {
              tor.rotation.z += 0.2 + r * 0.1;
              tor.scale.setScalar(1 + ease * (0.5 + r * 0.3));
            }
          }
          const flash = f.group.getObjectByName("fxflash") as THREE.Mesh | undefined;
          if (flash) flash.scale.setScalar(Math.max(0.05, 1 - t * 1.4));
          break;
        }
        default: {
          f.group.children.forEach((c) => {
            const p = c as THREE.Mesh;
            if (p.userData.vy) p.position.y += p.userData.vy;
          });
        }
      }

      // crit flash: quick pop then fade
      const crit = f.group.getObjectByName("fxcritflash") as THREE.Mesh | undefined;
      if (crit) crit.scale.setScalar(Math.max(0.05, 1 - t * 2));
    }
  }

  // ── Picking ──
  const raycaster = new THREE.Raycaster();
  const pickPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  function pickTarget(): { pos: Vec3; targetId: string | null } | null {
    // crosshair is screen center
    const ndc = new THREE.Vector2(0, 0);
    raycaster.setFromCamera(ndc, camera);

    // try creatures first (enemy faction only)
    if (selfId) {
      const self = [...entities.values()].find((e) => e.target.id === selfId);
      const enemyFaction: Faction = self && self.target.faction === "dawn" ? "dusk" : "dawn";
      const candidates: THREE.Object3D[] = [];
      for (const ev of entities.values()) {
        if (ev.target.kind === "creature" && ev.target.faction === enemyFaction) {
          candidates.push(ev.group);
        }
      }
      const hits = raycaster.intersectObjects(candidates, true);
      if (hits.length > 0 && hits[0].distance < 60) {
        const hit = hits[0];
        let obj: THREE.Object3D | null = hit.object;
        while (obj && !entitiesHasGroup(obj)) obj = obj.parent;
        const ev = obj ? [...entities.values()].find((e) => e.group === obj) : null;
        if (ev) {
          return {
            pos: { x: hit.point.x, y: 0, z: hit.point.z },
            targetId: ev.target.id,
          };
        }
      }
    }

    // fallback: ground plane
    const point = new THREE.Vector3();
    if (raycaster.ray.intersectPlane(pickPlane, point)) {
      point.x = THREE.MathUtils.clamp(point.x, -ARENA.WIDTH / 2, ARENA.WIDTH / 2);
      point.z = THREE.MathUtils.clamp(point.z, -ARENA.DEPTH / 2, ARENA.DEPTH / 2);
      return { pos: { x: point.x, y: 0, z: point.z }, targetId: null };
    }
    return null;
  }

  function entitiesHasGroup(obj: THREE.Object3D): boolean {
    for (const ev of entities.values()) {
      if (ev.group === obj) return true;
    }
    return false;
  }

  // ── Animation loop ──
  let raf = 0;
  let disposed = false;
  let lastFrame = performance.now();

  function animate() {
    if (disposed) return;
    raf = requestAnimationFrame(animate);

    const now = performance.now();
    // dt in "60fps frame units" so lerp factors are refresh-rate independent
    const dt = Math.min(0.1, (now - lastFrame) / 16.667);
    lastFrame = now;

    // ── Self: apply soft server correction to the local prediction ──
    // The server position is the source of truth for *where you really are*, but
    // we only use it to gently nudge the prediction (or snap on a large error
    // from a clamped/rejected move). This is what kills the rubber-banding.
    if (selfId && desiredPos && selfCorrection) {
      const errX = selfCorrection.x - desiredPos.x;
      const errZ = selfCorrection.z - desiredPos.z;
      const err = Math.sqrt(errX * errX + errZ * errZ);
      if (err > SOFT_CORRECT_DIST) {
        // large divergence: snap to the server (we were clamped or rejected)
        desiredPos.x = selfCorrection.x;
        desiredPos.z = selfCorrection.z;
      } else if (err > 0.001) {
        // small divergence: ease the prediction toward the server
        const k = 1 - Math.pow(1 - SOFT_CORRECT_RATE, dt);
        desiredPos.x += errX * k;
        desiredPos.z += errZ * k;
      }
    }

    // interpolate + bob entities + billboard hp bars
    for (const ev of entities.values()) {
      if (!ev.group.visible) continue;
      const isSelf = ev.target.id === selfId;
      if (isSelf && desiredPos) {
        // self renders directly from the (corrected) local prediction — no interp
        ev.group.position.x = desiredPos.x;
        ev.group.position.z = desiredPos.z;
      } else {
        // remote: smooth position between the last two server samples
        const span = ev.targetAt - ev.prevAt;
        let t = span > 0 ? (now - ev.prevAt - INTERP_DELAY_MS) / span : 1;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        ev.group.position.x = ev.prevX + (ev.targetX - ev.prevX) * t;
        ev.group.position.z = ev.prevZ + (ev.targetZ - ev.prevZ) * t;
      }
      const bob = Math.sin(now * 0.004 + ev.bobPhase) * 0.08;
      ev.group.position.y = bob;
      ev.hpBar.lookAt(camera.position);
    }

    // camera follow (dt-based so it feels identical at 60/120/144Hz)
    if (selfId) {
      const ev = entities.get(selfId);
      if (ev) {
        if (!hasSelf) {
          selfSmooth.copy(ev.group.position);
          hasSelf = true;
        }
        const target = desiredPos
          ? new THREE.Vector3(desiredPos.x, 0, desiredPos.z)
          : ev.group.position;
        const kSelf = 1 - Math.pow(1 - 0.18, dt);
        selfSmooth.lerp(target, kSelf);
        const camTarget = new THREE.Vector3(selfSmooth.x * 0.35, 72, 95);
        const kCam = 1 - Math.pow(1 - 0.08, dt);
        camera.position.lerp(camTarget, kCam);
        camera.lookAt(selfSmooth.x * 0.25, 0, -6);
      }
    }

    renderer.render(scene, camera);
  }
  animate();

  // ── Resize ──
  function onResize() {
    const { w, h } = sizeFrom();
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener("resize", onResize);
  // re-sync when the canvas's container is laid out / resizes
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined" && parent) {
    ro = new ResizeObserver(onResize);
    ro.observe(parent);
  }
  // initial sync after first layout pass
  requestAnimationFrame(onResize);

  return {
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      renderer.dispose();
    },
    applyState,
    setDesiredPos(x, z) {
      desiredPos = { x, z };
    },
    setSelfCorrection(x, z) {
      selfCorrection = { x, z };
    },
    pickTarget,
    setSelf(playerId) {
      selfId = playerId;
      hasSelf = false;
      selfCorrection = null;
    },
    getCameraPos: () => camera.position.clone(),
    projectToScreen(x: number, z: number): { x: number; y: number } | null {
      const v = new THREE.Vector3(x, 0, z).project(camera);
      // behind the camera
      if (v.z > 1) return null;
      const { w, h } = sizeFrom();
      return {
        x: (v.x * 0.5 + 0.5) * w,
        y: (-v.y * 0.5 + 0.5) * h,
      };
    },
  };
}
